import { GANHE_MAIS_CORRETOR_CLIENT_URL } from '../../../../constants/ClientUrls';
import { TERMS } from '../../../../constants/Routes';
import { useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import { withStyles } from '@material-ui/core/styles';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeIcon from '@mui/icons-material/Home';
import StoreIcon from '@mui/icons-material/Store';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import BalanceIcon from '@mui/icons-material/Balance';
import HttpsIcon from '@mui/icons-material/Https';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import ForumIcon from '@mui/icons-material/Forum';
import { useAuth } from '../../../../hooks/useAuth';
import { useHomeEquity } from '../../hooks/useHomeEquity';
import { useRating } from '../../../../hooks/useRating';
import { IHomeEquity, IHomeEquityReferrerInfo } from '../../types';
import { api } from '../../../../server/api';
import { Notify } from '../../../../components/Notify';
import { WhatsAppCapture } from '../../../../pages/Action/components/WhatsAppCapture';
import { CashMeFlow } from '../../cashme/components/CashMeFlow/CashMeFlow';
import './HomeEquityLanding.css';

const StyledRating = withStyles({
  iconFilled: { color: '#ffc107' },
})(Rating);

function AnimatedCounter({
  to,
  duration = 1.8,
}: {
  to: number;
  duration?: number;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!triggered) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * to));
      if (progress < 1) requestAnimationFrame(step);
      else setValue(to);
    };
    requestAnimationFrame(step);
  }, [triggered, to, duration]);

  return <span ref={ref}>{value}</span>;
}

const PROFILE_MAP: {
  key: string;
  ico: ReactNode;
  tag: string;
  label: string;
  desc: string;
  hl: boolean;
}[] = [
  {
    key: 'apartment',
    ico: <ApartmentIcon sx={{ fontSize: 28 }} />,
    tag: 'RESIDENCIAL',
    label: 'Dono de apartamento',
    desc: 'Usa o crédito para investir ou quitar dívidas.',
    hl: true,
  },
  {
    key: 'house',
    ico: <HomeIcon sx={{ fontSize: 28 }} />,
    tag: 'CASA',
    label: 'Dono de casa própria',
    desc: 'Crédito sem vender o imóvel.',
    hl: false,
  },
  {
    key: 'commercial',
    ico: <StoreIcon sx={{ fontSize: 28 }} />,
    tag: 'COMERCIAL',
    label: 'Dono de sala/loja',
    desc: 'Capital para expandir o negócio.',
    hl: false,
  },
  {
    key: 'rural',
    ico: <AgricultureIcon sx={{ fontSize: 28 }} />,
    tag: 'RURAL',
    label: 'Proprietário rural',
    desc: 'Crédito para negócios rurais.',
    hl: false,
  },
  {
    key: 'rentOut',
    ico: <HolidayVillageIcon sx={{ fontSize: 28 }} />,
    tag: 'INQUILINO',
    label: 'Proprietário que aluga',
    desc: 'Usa o patrimônio parado.',
    hl: false,
  },
  {
    key: 'heir',
    ico: <BalanceIcon sx={{ fontSize: 28 }} />,
    tag: 'HERANÇA',
    label: 'Herdeiro com imóvel',
    desc: 'Patrimônio que gera crédito.',
    hl: false,
  },
];

interface LeadForm {
  nome: string;
  sobrenome: string;
  tel: string;
  email: string;
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11;
}

export interface LeadContact {
  name: string;
  email: string;
  phone: string;
}

function FormularioCaptura({
  partnerName,
  playerName,
  playerId,
  clientId,
  publicationId,
  businessName,
  onLeadCreated,
}: {
  partnerName?: string;
  playerName?: string;
  playerId: string;
  clientId: string;
  publicationId: string;
  businessName?: string;
  onLeadCreated: (actionLeadId: string, contact: LeadContact) => void;
}) {
  const [form, setForm] = useState<LeadForm>({
    nome: '',
    sobrenome: '',
    tel: '',
    email: '',
  });
  const [errors, setErrors] = useState({
    tel: '',
    email: '',
  });

  const upd = (k: keyof LeadForm, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handlePhoneChange = (v: string) => {
    const masked = maskPhone(v);
    upd('tel', masked);
    if (errors.tel && isValidPhone(masked)) {
      setErrors((e) => ({ ...e, tel: '' }));
    }
  };

  const handleEmailChange = (v: string) => {
    upd('email', v);
    if (errors.email && isValidEmail(v)) {
      setErrors((e) => ({ ...e, email: '' }));
    }
  };

  const validate = (): boolean => {
    const next = { tel: '', email: '' };
    if (!isValidPhone(form.tel)) {
      next.tel = 'Informe um telefone válido: (00) 00000-0000';
    }
    if (!isValidEmail(form.email)) {
      next.email = 'Informe um e-mail válido: exemplo@dominio.com';
    }
    setErrors(next);
    return !next.tel && !next.email;
  };

  const handleSubmit = async () => {
    if (!form.nome || !form.tel || !form.email) return;
    if (!validate()) return;

    try {
      const leadData = {
        name: form.nome,
        lastname: form.sobrenome,
        email: form.email,
        phone: `+55 ${form.tel.replace(/\D/g, '')}`,
        businessId: publicationId,
        businessName: businessName ?? '',
        playerId,
        clientId,
        converted: false,
        leadToken:
          Math.random().toString(36).substr(2, 9) +
          Math.random().toString(36).substring(2, 9),
        acceptedTerms: 'true',
      };

      const parameters = {
        createFunctionality: 'registerLead',
        userType: 'consumer',
        lead: leadData,
        clientId,
        playerId,
        validationCode: '',
        urlOrigem: window.location.href,
      };

      const { data } = await api.post('/create', parameters);

      if (data.success) {
        onLeadCreated(data.data?.uid, {
          name: `${form.nome} ${form.sobrenome}`.trim(),
          email: form.email,
          phone: form.tel,
        });
      } else {
        throw new Error(data.message ?? 'Erro ao enviar simulação.');
      }
    } catch (error) {
      console.log('HomeEquity saveLead error:', error);
      Notify(
        error instanceof Error
          ? error.message
          : 'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente mais tarde.',
        'error'
      );
    }
  };

  return (
    <div className="lp-form-cta">
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'rgba(255,255,255,.7)',
          letterSpacing: 1,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        ✓ Análise gratuita e sem compromisso
      </p>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 900,
          color: '#fff',
          marginBottom: 4,
        }}
      >
        Faça sua simulação agora
      </h2>
      {playerName && (
        <p
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,.85)',
            lineHeight: 1.6,
          }}
        >
          Indicado por <strong style={{ color: '#fff' }}>{playerName}</strong>
          {partnerName && ` via ${partnerName}`} — análise personalizada.
        </p>
      )}
      <div className="lp-form-wrap">
        <div className="lp-form-field">
          <label>
            Nome <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Seu nome"
            value={form.nome}
            onChange={(e) => upd('nome', e.target.value)}
          />
        </div>
        <div className="lp-form-field">
          <label>Sobrenome</label>
          <input
            type="text"
            placeholder="Seu sobrenome"
            value={form.sobrenome}
            onChange={(e) => upd('sobrenome', e.target.value)}
          />
        </div>
        <div className="lp-form-field">
          <label>
            Telefone / WhatsApp <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            type="tel"
            placeholder="(00) 00000-0000"
            value={form.tel}
            onChange={(e) => handlePhoneChange(e.target.value)}
            onBlur={() => {
              if (form.tel && !isValidPhone(form.tel)) {
                setErrors((e) => ({
                  ...e,
                  tel: 'Informe um telefone válido: (00) 00000-0000',
                }));
              }
            }}
            style={errors.tel ? { borderColor: '#DC2626' } : undefined}
          />
          {errors.tel && (
            <span style={{ fontSize: 10, color: '#DC2626', marginTop: 2 }}>
              {errors.tel}
            </span>
          )}
        </div>
        <div className="lp-form-field">
          <label>
            E-mail <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            type="email"
            placeholder="seu@email.com"
            value={form.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={() => {
              if (form.email && !isValidEmail(form.email)) {
                setErrors((e) => ({
                  ...e,
                  email: 'Informe um e-mail válido: exemplo@dominio.com',
                }));
              }
            }}
            style={errors.email ? { borderColor: '#DC2626' } : undefined}
          />
          {errors.email && (
            <span style={{ fontSize: 10, color: '#DC2626', marginTop: 2 }}>
              {errors.email}
            </span>
          )}
        </div>
        <button className="lp-submit" onClick={handleSubmit}>
          ✓ FAZER SIMULAÇÃO GRÁTIS
        </button>
        <p
          style={{
            fontSize: 10,
            color: '#7A96BF',
            lineHeight: 1.6,
            textAlign: 'center',
          }}
        >
          Seus dados são protegidos pela LGPD.
        </p>
      </div>
    </div>
  );
}

const DEFAULT_RATES = [
  {
    lbl: 'Cartão de crédito',
    rate: '~15% a.m.',
    bar: 100,
    variant: 'bad' as const,
  },
  {
    lbl: 'Empréstimo pessoal',
    rate: '~5% a.m.',
    bar: 65,
    variant: 'bad' as const,
  },
  {
    lbl: 'Consórcio',
    rate: '~1,5% a.m.',
    bar: 28,
    variant: 'neutral' as const,
  },
  {
    lbl: 'Home Equity',
    rate: 'a partir de 0,89%',
    bar: 12,
    variant: 'good' as const,
  },
];

const DEFAULT_TESTIMONIALS = [
  {
    name: 'Carlos M.',
    city: 'São Paulo – SP',
    text: 'Em 3 dias já tinha a aprovação. Quitei todas as dívidas e a parcela ficou 60% menor.',
    result: '✓ Crédito aprovado em R$ 320.000',
  },
  {
    name: 'Ana R.',
    city: 'Recife – PE',
    text: 'Precisava de capital para meu negócio. Consegui taxas que nunca teria em nenhum banco.',
    result: '✓ Taxa de 0,89% a.m. aprovada',
  },
  {
    name: 'Pedro L.',
    city: 'Curitiba – PR',
    text: 'Usei o crédito para comprar outro imóvel à vista. Melhor decisão financeira que tomei.',
    result: '✓ R$ 450.000 aprovados',
  },
];

export function HomeEquity() {
  const { homeEquityId, playerId } = useParams<{
    homeEquityId: string;
    playerId?: string;
  }>();
  const { client, player, getClientByClientIdAndPlayerId } = useAuth();
  const { getHomeEquityById, getHomeEquityPage } = useHomeEquity();
  const { getRating, rating } = useRating();

  const [he, setHe] = useState<IHomeEquity | null>(null);
  const [referrer, setReferrer] = useState<IHomeEquityReferrerInfo | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);
  const [fctaShow, setFctaShow] = useState(false);
  const [isOpenWhatsAppCapture, setIsOpenWhatsAppCapture] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const [ratesVisible, setRatesVisible] = useState(false);
  const [cashMeLead, setCashMeLead] = useState<{
    actionLeadId: string;
    contact: LeadContact;
  } | null>(null);

  const handoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLeadCreated = useCallback(
    (actionLeadId: string, contact: LeadContact) => {
      if (!actionLeadId) {
        Notify(
          'Não foi possível iniciar a simulação. Por favor, tente novamente.',
          'error'
        );
        return;
      }
      setCashMeLead({ actionLeadId, contact });
    },
    []
  );

  useEffect(
    () => () => {
      if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!homeEquityId) return;
    setLoading(true);

    if (playerId) {
      getHomeEquityPage(homeEquityId, playerId)
        .then((data) => {
          setHe(data?.homeEquity ?? null);
          setReferrer(data?.referrer ?? null);
          getRating(playerId);
          const cId = data?.referrer?.clientId ?? data?.homeEquity?.clientId;
          if (cId) {
            getClientByClientIdAndPlayerId(cId, playerId);
          }
        })
        .finally(() => setLoading(false));
    } else if (client?.id) {
      getHomeEquityById(homeEquityId, client.id)
        .then((data) => setHe(data ?? null))
        .finally(() => setLoading(false));
      if (player?.id) getRating(player.id);
    }
  }, [homeEquityId, playerId, client?.id]);

  const imgs = he?.medias?.images?.length ? he.medias.images : [];
  const nSlides = imgs.length;

  useEffect(() => {
    if (!nSlides) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % nSlides), 4500);
    return () => clearInterval(t);
  }, [nSlides]);

  useEffect(() => {
    if (trackRef.current && nSlides) {
      trackRef.current.scrollTo({
        left: slide * trackRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  }, [slide]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY >= 300) setFctaShow(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const ratesRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRatesVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
  }, []);

  const scrollToForm = useCallback(() => {
    document
      .querySelector('.lp-form-cta')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <p style={{ color: '#7A96BF', fontSize: 14 }}>Carregando...</p>
      </div>
    );
  }

  if (cashMeLead) {
    return (
      <CashMeFlow
        actionLeadId={cashMeLead.actionLeadId}
        leadName={cashMeLead.contact.name}
        leadEmail={cashMeLead.contact.email}
        leadPhone={cashMeLead.contact.phone}
      />
    );
  }

  if (!he) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <p style={{ color: '#7A96BF', fontSize: 14 }}>
          Publicação não encontrada.
        </p>
      </div>
    );
  }

  const {
    settings,
    broker,
    product,
    rates,
    highlights,
    referred,
    referredTestimonials,
    marketingIntegrations,
  } = he;
  const partnerName = settings.financialPartner ?? 'Home Equity';
  const playerName = referrer?.name ?? player?.name;
  const playerAvatar = referrer?.photoURL ?? player?.photoURL;
  const effectivePlayerId = playerId ?? player?.id ?? '';
  const effectiveClientId =
    referrer?.clientId ?? he.clientId ?? client?.id ?? '';

  const botActive = client?.isActiveBotWhatsApp === 'true' && !!playerId;

  const saveLeadWhatsApp = async (e: {
    name: string;
    email: string;
    phone: string;
  }) => {
    try {
      const leadData = {
        name: e.name,
        lastname: '',
        email: e.email,
        phone: `+55 ${e.phone.replace(/\D/g, '')}`,
        businessId: homeEquityId,
        businessName: broker?.title ?? '',
        playerId: effectivePlayerId,
        clientId: effectiveClientId,
        converted: false,
        leadToken:
          Math.random().toString(36).substr(2, 9) +
          Math.random().toString(36).substring(2, 9),
        acceptedTerms: 'true',
      };
      const parameters = {
        createFunctionality: 'registerLead',
        userType: 'consumer',
        lead: leadData,
        clientId: effectiveClientId,
        playerId: effectivePlayerId,
        validationCode: '',
        urlOrigem: window.location.href,
      };

      const { data } = await api.post('/create', parameters);

      if (data.success) {
        const actionLeadId = data.data?.uid;

        if (!actionLeadId) {
          throw new Error(
            'Não foi possível iniciar a simulação. Por favor, tente novamente.'
          );
        }

        handoffTimerRef.current = setTimeout(() => {
          setIsOpenWhatsAppCapture(false);
          handleLeadCreated(actionLeadId, {
            name: e.name,
            email: e.email,
            phone: e.phone,
          });
        }, 4000);
      } else {
        throw new Error(data.message ?? 'Erro ao enviar simulação.');
      }
    } catch (error) {
      console.log('HomeEquity WhatsApp saveLead error:', error);
      Notify(
        error instanceof Error
          ? error.message
          : 'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente mais tarde.',
        'error'
      );
    }
  };

  const heroPills: string[] = [];
  if (product?.minRate) heroPills.push(`✓ A partir de ${product.minRate}%/mês`);
  if (product?.maxTermMonths)
    heroPills.push(`✓ Até ${product.maxTermMonths} meses`);
  if (product?.maxLtvPercent)
    heroPills.push(`✓ Até ${product.maxLtvPercent}% do imóvel`);
  if (product?.approvalDays)
    heroPills.push(`✓ Aprovação em ${product.approvalDays} dias úteis`);

  const credStats = [
    highlights?.signedContracts
      ? [highlights.signedContracts, 'contratos assinados']
      : null,
    highlights?.originatedVolume
      ? [highlights.originatedVolume, 'volume originado']
      : null,
    product?.minRate ? [`${product.minRate}%`, 'taxa a partir de a.m.'] : null,
    product?.approvalDays
      ? [`${product.approvalDays} dias`, 'prazo de aprovação']
      : null,
  ].filter(Boolean) as [string, string][];

  const activeProfiles = PROFILE_MAP.filter(
    (p) =>
      referred?.targetProfiles?.[
        p.key as keyof typeof referred.targetProfiles
      ] !== false
  );

  const ratesData = [
    rates?.creditCard != null
      ? {
          lbl: 'Cartão de crédito',
          rate: `~${rates.creditCard}% a.m.`,
          bar: 100,
          variant: 'bad' as const,
        }
      : null,
    rates?.personalLoan != null
      ? {
          lbl: 'Empréstimo pessoal',
          rate: `~${rates.personalLoan}% a.m.`,
          bar: 65,
          variant: 'bad' as const,
        }
      : null,
    rates?.consortium != null
      ? {
          lbl: 'Consórcio',
          rate: `~${rates.consortium}% a.m.`,
          bar: 28,
          variant: 'neutral' as const,
        }
      : null,
    rates?.homeEquity != null
      ? {
          lbl: `Home Equity (${partnerName})`,
          rate: `a partir de ${rates.homeEquity}%`,
          bar: 12,
          variant: 'good' as const,
        }
      : null,
  ].filter(Boolean) as {
    lbl: string;
    rate: string;
    bar: number;
    variant: 'good' | 'bad' | 'neutral';
  }[];

  return (
    <>
      <div
        className="lp-he"
        style={{ background: '#F0F4F8', minHeight: '100dvh' }}
      >
        {/* Marketing integrations */}
        {marketingIntegrations?.isIntegrationPixel &&
          marketingIntegrations.integrationPixelCode && (
            <img
              src={`https://www.facebook.com/tr?id=${marketingIntegrations.integrationPixelCode}&ev=PageView&noscript=1`}
              style={{ display: 'none' }}
              alt=""
            />
          )}

        {/* Header — player/broker info */}
        <div className="lp-corretor-header">
          <div className="lp-corretor-brand">
            <span
              style={{
                fontSize: 20,
                fontWeight: 900,
                color: '#0A1F5C',
                letterSpacing: -1,
                lineHeight: 1,
              }}
            >
              {partnerName}
            </span>
            <span
              style={{
                fontSize: 8,
                color: '#7A96BF',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginTop: 2,
              }}
            >
              home equity
            </span>
          </div>
          {playerAvatar && (
            <div className="lp-corretor-av-wrap">
              <img src={playerAvatar} alt={playerName} />
            </div>
          )}
          {rating?.averageRating > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginTop: 6,
              }}
            >
              <StyledRating
                value={Number(rating.averageRating)}
                precision={0.1}
                readOnly
                size="small"
              />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0A1F5C' }}>
                {Number(rating.averageRating).toFixed(1)}
              </span>
            </div>
          )}
          {playerName && (
            <p
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#0A1F5C',
                marginTop: 4,
              }}
            >
              {playerName}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#7A96BF', marginTop: 2 }}>
            tem uma solução de crédito para você
          </p>
        </div>

        {/* Security bar */}
        <div className="lp-secbar">
          {[
            referred?.trustBadges?.ssl !== false && {
              icon: <HttpsIcon sx={{ fontSize: 14 }} />,
              label: 'Ambiente 100% seguro',
            },
            referred?.trustBadges?.bcb !== false && {
              icon: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
              label: 'Regulado pelo Banco Central',
            },
            referred?.trustBadges?.lgpd !== false && {
              icon: <AssignmentIcon sx={{ fontSize: 14 }} />,
              label: 'Conforme LGPD',
            },
          ]
            .filter(Boolean)
            .map((item) => {
              const { icon, label } = item as {
                icon: ReactNode;
                label: string;
              };
              return (
                <div key={label} className="lp-secitem">
                  {icon}
                  {label}
                </div>
              );
            })}
        </div>

        {/* Hero */}
        <div className="lp-hero">
          <div className="lp-hero-z">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <div className="lp-logo-chip">
                <span
                  style={{ fontSize: 13, fontWeight: 900, color: '#0A1F5C' }}
                >
                  {partnerName}
                </span>
              </div>
              <div className="lp-hero-tag">HOME EQUITY</div>
            </div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: '#fff',
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              {referred?.title ?? 'Crédito com as menores taxas do mercado'}
            </h1>
            <p
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,.88)',
                lineHeight: 1.7,
                marginBottom: 4,
              }}
            >
              {referred?.heroDescription ??
                'Use seu imóvel como garantia e acesse crédito sem precisar vendê-lo.'}
            </p>
            <div className="lp-hero-avatar-pills">
              {playerName && (
                <div className="lp-corretor-chip">
                  {playerAvatar && (
                    <img
                      src={playerAvatar}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '1.5px solid rgba(255,255,255,.5)',
                      }}
                      alt=""
                    />
                  )}
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'rgba(255,255,255,.9)',
                    }}
                  >
                    Indicado por {playerName}
                  </span>
                </div>
              )}
              {heroPills.length > 0 && (
                <div className="lp-hero-pills">
                  {heroPills.map((p) => (
                    <div key={p} className="lp-hero-pill">
                      {p}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {imgs.length > 0 && (
            <div className="lp-carousel">
              <div className="lp-c-track" ref={trackRef}>
                {imgs.map((src, i) => (
                  <div key={i} className="lp-c-slide">
                    <img src={src} alt="" />
                  </div>
                ))}
              </div>
              {nSlides > 1 && (
                <>
                  <button
                    className="lp-c-arrow"
                    style={{ left: 10 }}
                    onClick={() => setSlide((s) => Math.max(0, s - 1))}
                  >
                    ‹
                  </button>
                  <button
                    className="lp-c-arrow"
                    style={{ right: 10 }}
                    onClick={() =>
                      setSlide((s) => Math.min(nSlides - 1, s + 1))
                    }
                  >
                    ›
                  </button>
                  <div className="lp-c-dots">
                    {imgs.map((_, i) => (
                      <button
                        key={i}
                        className={`lp-c-dot${slide === i ? ' active' : ''}`}
                        onClick={() => setSlide(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="lp-content">
          <div className="lp-content-grid">
            {/* Main column */}
            <div className="lp-content-main">
              {/* Credibility numbers */}
              {credStats.length > 0 && (
                <div className="lp-sec">
                  <div className="lp-cred-grid">
                    {credStats.map(([v, l]) => (
                      <div key={l} className="lp-cred-card">
                        <p
                          style={{
                            fontSize: 22,
                            fontWeight: 900,
                            color: '#0A1F5C',
                            lineHeight: 1,
                          }}
                        >
                          {v}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: '#7A96BF',
                            marginTop: 4,
                          }}
                        >
                          {l}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Target profiles */}
              {activeProfiles.length > 0 && (
                <div className="lp-sec">
                  <div className="lp-card">
                    <div className="lp-cp">
                      <h2
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <TrackChangesIcon
                          sx={{ fontSize: 18, color: '#0A1F5C' }}
                        />
                        Para quem é o Home Equity?
                      </h2>
                      <p
                        style={{
                          fontSize: 12,
                          color: '#7A96BF',
                          marginBottom: 13,
                        }}
                      >
                        {referred?.heroDescription ??
                          'Qualquer pessoa com imóvel no nome — residencial, comercial, rural ou de herança.'}
                      </p>
                      <div className="lp-profiles">
                        {activeProfiles.map((p) => (
                          <div
                            key={p.key}
                            className={p.hl ? 'lp-pcard-hl' : 'lp-pcard-nm'}
                          >
                            <span
                              style={{
                                display: 'block',
                                marginBottom: 7,
                                color: '#0A1F5C',
                              }}
                            >
                              {p.ico}
                            </span>
                            <span
                              style={{
                                display: 'inline-block',
                                borderRadius: 100,
                                padding: '2px 8px',
                                fontSize: 8,
                                fontWeight: 700,
                                background: p.hl ? '#0A1F5C' : '#C8D9F0',
                                color: p.hl ? '#fff' : '#7A96BF',
                                marginBottom: 6,
                              }}
                            >
                              {p.tag}
                            </span>
                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                marginBottom: 3,
                              }}
                            >
                              {p.label}
                            </p>
                            <p
                              style={{
                                fontSize: 10,
                                color: '#7A96BF',
                                lineHeight: 1.6,
                              }}
                            >
                              {p.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rate comparison */}
              {(() => {
                const bars = ratesData.length > 0 ? ratesData : DEFAULT_RATES;
                const ltv = product?.maxLtvPercent ?? 60;
                const term = product?.maxTermMonths ?? 240;
                return (
                  <div className="lp-sec">
                    <div
                      style={{
                        background: '#0d1629',
                        borderRadius: 18,
                        padding: 20,
                        marginBottom: 16,
                      }}
                    >
                      <div
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 16,
                          padding: 20,
                          marginBottom: 16,
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: 700,
                            marginBottom: 20,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#f0f4ff',
                            fontSize: 15,
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#00BFFF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="20" x2="18" y2="10" />
                            <line x1="12" y1="20" x2="12" y2="4" />
                            <line x1="6" y1="20" x2="6" y2="14" />
                          </svg>
                          Comparativo de taxas
                        </h4>
                        <div
                          ref={ratesRef}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                          }}
                        >
                          {bars.map((b, idx) => (
                            <div key={b.lbl}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: 6,
                                  fontSize: b.variant === 'good' ? 14 : 12,
                                  fontWeight: b.variant === 'good' ? 700 : 400,
                                  color:
                                    b.variant === 'good'
                                      ? '#00BFFF'
                                      : 'rgba(240,244,255,0.6)',
                                }}
                              >
                                <span>{b.lbl}</span>
                                <span>{b.rate}</span>
                              </div>
                              <div
                                style={{
                                  height: 12,
                                  background: 'rgba(255,255,255,0.1)',
                                  borderRadius: 999,
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    borderRadius: 999,
                                    width: ratesVisible ? `${b.bar}%` : '0%',
                                    transition: `width 1.1s ease-out ${idx * 0.15}s`,
                                    background:
                                      b.variant === 'good'
                                        ? 'linear-gradient(to right, #00BFFF, #67e8f9)'
                                        : b.variant === 'bad'
                                          ? 'rgba(248,113,113,0.7)'
                                          : 'rgba(255,255,255,0.3)',
                                    boxShadow:
                                      b.variant === 'good'
                                        ? '0 0 8px rgba(0,191,255,0.6)'
                                        : 'none',
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div
                        style={{
                          background: 'rgba(0,191,255,0.08)',
                          border: '1px solid rgba(0,191,255,0.25)',
                          borderRadius: 16,
                          padding: 24,
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 52,
                            fontWeight: 900,
                            color: '#00BFFF',
                            lineHeight: 1.1,
                            marginBottom: 4,
                          }}
                        >
                          Até <AnimatedCounter to={ltv} duration={1.6} />%
                        </div>
                        <p
                          style={{
                            fontSize: 14,
                            color: '#f0f4ff',
                            margin: '0 0 16px',
                          }}
                        >
                          do valor do imóvel liberado em crédito
                        </p>
                        <div
                          style={{
                            borderTop: '1px solid rgba(0,191,255,0.2)',
                            margin: '0 0 16px',
                          }}
                        />
                        <div
                          style={{
                            fontSize: 44,
                            fontWeight: 900,
                            color: '#ffffff',
                            lineHeight: 1.1,
                            marginBottom: 4,
                          }}
                        >
                          <AnimatedCounter to={term} duration={2.0} /> meses
                        </div>
                        <p
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#00BFFF',
                            margin: 0,
                          }}
                        >
                          para pagar — parcelas que cabem no bolso
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Explainer video */}
              {referred?.explainerVideoUrl && (
                <div className="lp-sec">
                  <div className="lp-card">
                    <div className="lp-cp" style={{ paddingBottom: 10 }}>
                      <h2
                        style={{
                          marginBottom: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <OndemandVideoIcon
                          sx={{ fontSize: 18, color: '#0A1F5C' }}
                        />
                        Entenda em 3 minutos
                      </h2>
                      <p style={{ fontSize: 12, color: '#7A96BF' }}>
                        Assista e tire todas as suas dúvidas antes de simular.
                      </p>
                    </div>
                    <div
                      className="lp-video-thumb"
                      onClick={() =>
                        window.open(referred.explainerVideoUrl, '_blank')
                      }
                    >
                      <div
                        className="lp-video-bg"
                        style={{ backgroundImage: `url(${imgs[0] ?? ''})` }}
                      />
                      <div className="lp-video-ov">
                        <div className="lp-play-btn">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                        <p
                          style={{
                            color: 'rgba(255,255,255,.88)',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Assistir vídeo explicativo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Testimonials */}
              {(() => {
                const deps =
                  (referredTestimonials?.length ?? 0) > 0
                    ? referredTestimonials!
                    : DEFAULT_TESTIMONIALS;
                return (
                  <div className="lp-sec">
                    <h2
                      style={{
                        fontSize: 15,
                        fontWeight: 800,
                        marginBottom: 5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <ForumIcon sx={{ fontSize: 18, color: '#0A1F5C' }} />
                      Quem já usou recomenda
                    </h2>
                    <p
                      style={{
                        fontSize: 12,
                        color: '#7A96BF',
                        marginBottom: 14,
                      }}
                    >
                      Histórias reais de clientes que transformaram o patrimônio
                      em crédito.
                    </p>
                    <div className="lp-deps-wrap">
                      {deps.map((t, i) => (
                        <div key={i} className="lp-dep">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 10,
                            }}
                          >
                            <div>
                              <p style={{ fontSize: 12, fontWeight: 800 }}>
                                {t.name}
                              </p>
                              <p style={{ fontSize: 10, color: '#7A96BF' }}>
                                {t.city}
                              </p>
                            </div>
                            <StyledRating value={5} readOnly size="small" />
                          </div>
                          <p
                            style={{
                              fontSize: 12,
                              color: '#2C4A7C',
                              lineHeight: 1.8,
                              fontStyle: 'italic',
                              marginBottom: t.result ? 9 : 0,
                            }}
                          >
                            "{t.text}"
                          </p>
                          {t.result && (
                            <div
                              style={{
                                padding: '7px 11px',
                                background: '#DCFCE7',
                                border: '1px solid rgba(22,163,74,.2)',
                                borderRadius: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: '#15803D',
                                }}
                              >
                                ✓ {t.result}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Form — mobile */}
              {/* <div className="lp-sec" style={{ display: 'block' }}>
              <FormularioCaptura
                partnerName={partnerName}
                playerName={playerName}
                onLeadCreated={onLeadCreated}
              />
            </div> */}
            </div>

            {/* Sidebar — form sticky (tablet+) */}
            <div className="lp-content-side" id="form-conversion">
              <FormularioCaptura
                partnerName={partnerName}
                playerName={playerName}
                playerId={effectivePlayerId}
                clientId={effectiveClientId}
                publicationId={homeEquityId ?? ''}
                businessName={broker?.title}
                onLeadCreated={handleLeadCreated}
              />
              <div className="lp-selos">
                {[
                  referred?.trustBadges?.ssl !== false && {
                    icon: <HttpsIcon sx={{ fontSize: 14 }} />,
                    label: 'SSL Seguro',
                  },
                  referred?.trustBadges?.bcb !== false && {
                    icon: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
                    label: 'Banco Central',
                  },
                  referred?.trustBadges?.lgpd !== false && {
                    icon: <AssignmentIcon sx={{ fontSize: 14 }} />,
                    label: 'LGPD',
                  },
                ]
                  .filter(Boolean)
                  .map((item) => {
                    const { icon, label } = item as {
                      icon: ReactNode;
                      label: string;
                    };
                    return (
                      <div key={label} className="lp-selo">
                        {icon}
                        {label}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderTop: '0.5px solid #C8D9F0',
              padding: '24px 16px',
              marginTop: 20,
            }}
          >
            {/* Logo + parceiro */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 14,
                paddingBottom: 14,
                borderBottom: '0.5px solid #C8D9F0',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: '#0A1F5C',
                    letterSpacing: -0.5,
                    lineHeight: 1,
                  }}
                >
                  {partnerName}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: '#7A96BF',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    marginTop: 2,
                  }}
                >
                  Home Equity
                </div>
              </div>
              {client?.name && (
                <>
                  <div
                    style={{ height: 32, width: 0.5, background: '#C8D9F0' }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#2C4A7C',
                      }}
                    >
                      {client.name}
                    </div>
                    <div
                      style={{ fontSize: 9, color: '#7A96BF', marginTop: 1 }}
                    >
                      Crédito com garantia de imóvel
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Links legais */}
            <div
              style={{
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
                marginBottom: 10,
              }}
            >
              <a
                href={
                  client?.clientUrl === GANHE_MAIS_CORRETOR_CLIENT_URL
                    ? 'https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoliticaPrivacidade_GanheMaisCorretor_CORRETOR.docx.pdf?alt=media&token=0d3cc869-b87e-4c60-b611-b4313265034d'
                    : 'https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPOLI%CC%81TICA%20DE%20PRIVACIDADE%20LGPD%20COMPLIANCE%202026.pdf?alt=media&token=f6339049-9c75-4ac2-8417-a70c9d75be94'
                }
                target="_blank"
                style={{
                  fontSize: 11,
                  color: '#7A96BF',
                  textDecoration: 'none',
                }}
              >
                Política de privacidade
              </a>
              <Link
                to="/infos"
                style={{
                  fontSize: 11,
                  color: '#7A96BF',
                  textDecoration: 'none',
                }}
              >
                Termos de uso
              </Link>
            </div>

            {/* Copyright + disclaimer */}
            <div style={{ fontSize: 10, color: '#7A96BF' }}>
              © {new Date().getFullYear()} {client?.name ?? 'OQ Digital'} —
              Todos os direitos reservados
            </div>
            <div style={{ fontSize: 10, color: '#7A96BF', marginTop: 3 }}>
              *A consulta ao SCR/Bacen é realizada apenas após seu consentimento
              explícito.
            </div>
          </div>
        </div>
      </div>

      {botActive && (
        <WhatsAppCapture
          data={saveLeadWhatsApp}
          isOpen={isOpenWhatsAppCapture}
          client={client}
          player={{ name: referrer?.name, photoURL: referrer?.photoURL }}
          host={{}}
          termsUrl={`${TERMS}?clientId=${client?.id}`}
        />
      )}

      {createPortal(
        <div className={`lp-fcta${fctaShow && !botActive ? ' show' : ''}`}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: '#0A1F5C',
                lineHeight: 1.3,
                marginBottom: 2,
              }}
            >
              Simule agora — gratuito!
            </p>
            {product?.minRate && (
              <span style={{ fontSize: 10, color: '#0057A8', fontWeight: 600 }}>
                A partir de {product.minRate}%/mês · Sem compromisso
              </span>
            )}
          </div>
          <button className="lp-fcta-btn" onClick={scrollToForm}>
            Simular grátis
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
